/**
 * `object_strip` — a set of peers, read one of two ways.
 *
 * The graph names the way in `layout`, and honouring it is the whole point:
 * `strip` is the sideways run of photographs a catalogue answer becomes (a list
 * of thirteen fountains as text rows is a directory; the same thirteen as tiles
 * are the park), while `column` is a list of rows — the museum's own source
 * pages, which carry a URL and no picture at all. Drawing those as tiles would
 * put empty frames under an answer.
 *
 * A tap sends the phrase the graph put on the tile, unchanged.
 */

import type { ObjectStripProps } from '../generated/schemas';
import { send } from '../../lib/send';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import { useSendLocked } from '../../chat/turnLock';

type Tile = ObjectStripProps['tiles'][number];

function Body({ tile }: { tile: Tile }) {
  return (
    <>
      {tile.image ? (
        <img
          className="strip__image"
          src={tile.image}
          alt=""
          loading="lazy"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <div className="strip__image strip__image--blank" aria-hidden="true" />
      )}
      <span className="strip__title">{plain(tile.label)}</span>
      {/* A distance, when `find_nearby` has one, rides in `sublabel` — the
          catalogue has no separate field for it, and inventing one here would be
          a prop the backend cannot fill. */}
      {tile.sublabel ? <span className="strip__sub">{plain(tile.sublabel)}</span> : null}
    </>
  );
}

/** A row: label, optional sublabel, and whatever tapping it does. */
function Row({ tile }: { tile: Tile }) {
  const sendLocked = useSendLocked();
  const inner = (
    <>
      <span className="rows__title">{plain(tile.label)}</span>
      {tile.sublabel ? <span className="rows__sub">{plain(tile.sublabel)}</span> : null}
    </>
  );
  if (tile.url) {
    return (
      <li className="rows__item">
        <a className="rows__link" href={tile.url} target="_blank" rel="noreferrer noopener">
          {inner}
        </a>
      </li>
    );
  }
  return (
    <li className="rows__item">
      <button
        type="button"
        className="rows__link"
        disabled={sendLocked}
        onClick={() => send(tile.value ?? `Расскажи про «${tile.label}»`)}
      >
        {inner}
      </button>
    </li>
  );
}

/**
 * A tile's `id` is the GRAPH's and is not unique within a strip — live on dev a
 * catalogue answer carried «Вольеры» twice, and React then warns about
 * duplicate keys and is free to drop one of the two. The position disambiguates
 * it; the id still carries the identity for anything that reads it.
 */
const tileKey = (tile: Tile, index: number) => `${tile.id}:${index}`;

export default function ObjectStrip({ props }: { props: ObjectStripProps }) {
  const column = props.layout === 'column';
  const sendLocked = useSendLocked();

  return (
    <article className={cn('card', column ? 'card--sources' : 'card--strip')}>
      {props.title ? (
        <header className="card__head">
          <h3 className="card__title">{plain(props.title)}</h3>
        </header>
      ) : null}

      {column ? (
        <ul className="rows">
          {props.tiles.map((tile, index) => (
            <Row key={tileKey(tile, index)} tile={tile} />
          ))}
        </ul>
      ) : (
        <ul className="strip">
          {props.tiles.map((tile, index) => (
            <li key={tileKey(tile, index)} className="strip__item">
              {tile.url ? (
                <a className="strip__tile" href={tile.url} target="_blank" rel="noreferrer noopener">
                  <Body tile={tile} />
                </a>
              ) : (
                <button
                  type="button"
                  className="strip__tile"
                  disabled={sendLocked}
                  onClick={() => send(tile.value ?? `Расскажи про «${tile.label}»`)}
                >
                  <Body tile={tile} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
